import type {
  SubscriptionResponse,
} from '../services/api'

/**
 * `personal-inbox-explicit-opt-in` (PostHog experiment 548486): whether the
 * connect modal asks for an explicit "add this to my subscription" tick before
 * the provider buttons unlock, and says when the add-on starts charging.
 *
 * Only the exact 'test' string treats, so a missing, boolean, or unknown
 * assignment is control and deactivating the flag is an instant rollback.
 */
export function requiresExplicitAddonOptIn(variant: unknown): boolean {
  return variant === 'test'
}

const BILLABLE_STATUSES = new Set(['trialing', 'active', 'past_due'])

/**
 * Whether connecting an inbox would charge the owner's own card: a live Stripe
 * subscription of their own, which the backend's `resolve_addon_billing_target`
 * always bills first. A granted or free subscription connects unbilled, and a
 * team-covered member (no Stripe id of their own) is left out too: org
 * membership alone (`billing_managed_by_team`) says nothing about whether the
 * org's plan covers them, so it can't tell us whether a connect bills anyone.
 * Leaving both out keeps the experiment to people who could be surprised by a
 * charge on their own bill, and keeps the copy below true for all of them.
 */
export function isBilledSubscription(
  subscription: SubscriptionResponse | null | undefined,
): boolean {
  if (!subscription?.is_active) return false
  return Boolean(subscription.stripe_subscription_id)
    && BILLABLE_STATUSES.has(subscription.status ?? '')
}

export type AddonPrice = { price_formatted: string, interval: string }

function formatDay(iso: string, locale?: string): string {
  return new Date(iso).toLocaleDateString(locale, { month: 'long', day: 'numeric' })
}

/**
 * When the add-on's first charge lands, in the owner's terms. A trialing
 * subscription has the item attached with no charge until the trial converts;
 * a paid one is invoiced the part-month on connect (`always_invoice`).
 */
export function describeAddonChargeStart(
  price: AddonPrice,
  subscription: SubscriptionResponse | null | undefined,
  locale?: string,
): string {
  const perInbox = `${price.price_formatted}/${price.interval}`
  if (subscription?.status === 'trialing') {
    const until = subscription.trial_end
      ? `Free until your trial ends on ${formatDay(subscription.trial_end, locale)}. `
      : 'Free until your trial ends. '
    return `${until}From then, ${perInbox} for each inbox is added to your Catch bill.`
  }
  return `Charged today for the rest of this billing period, then ${perInbox} `
    + 'for each inbox on your Catch bill.'
}

export function addonOptInLabel(price: AddonPrice): string {
  return `Add the Personal Inbox add-on (${price.price_formatted}/${price.interval}) `
    + 'to my subscription'
}
